import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { isMobile, isTablet } from '../../utils/deviceUtils';

const popularPackages = [
  {
    id: 1,
    name: 'Serengeti National Park',
    location: 'Tanzania',
    image: 'https://images.pexels.com/photos/4577791/pexels-photo-4577791.jpeg',
    duration: '5 Days 4 Night',
    rating: 4.9,
    reviews: 1289,
    price: 1299,
    description: 'Experience the great migration and endless plains',
  },
  {
    id: 2,
    name: 'Ngorongoro Crater',
    location: 'Tanzania',
    image: 'https://images.pexels.com/photos/4577801/pexels-photo-4577801.jpeg',
    duration: '3 Days 2 Night',
    rating: 4.8,
    reviews: 956,
    price: 899,
    description: "World's largest intact volcanic caldera",
  },
  {
    id: 3,
    name: 'Mount Kilimanjaro',
    location: 'Tanzania',
    image: 'https://images.pexels.com/photos/631317/pexels-photo-631317.jpeg',
    duration: '7 Days 6 Night',
    rating: 4.7,
    reviews: 832,
    price: 1899,
    description: "Africa's highest peak adventure",
  },
  {
    id: 4,
    name: 'Tarangire National Park',
    location: 'Tanzania',
    image: 'https://images.pexels.com/photos/4553618/pexels-photo-4553618.jpeg',
    duration: '4 Days 3 Night',
    rating: 4.6,
    reviews: 654,
    price: 1099,
    description: 'Famous for elephant herds and baobab trees',
  },
];

export const PopularPackages: React.FC = () => {
  const mobile = isMobile();
  const tablet = isTablet();

  return (
    <section
      className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-background-primary`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2
            className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-4xl'} font-bold text-text-primary mb-4`}
          >
            Popular Safari Packages
          </h2>
          <p
            className={`${mobile ? 'text-base' : 'text-lg'} text-text-secondary max-w-2xl mx-auto`}
          >
            Discover our most sought-after safari experiences, carefully curated
            to provide unforgettable adventures in the heart of Africa.
          </p>
        </div>

        <div
          className={`grid ${mobile ? 'grid-cols-1 gap-6' : tablet ? 'grid-cols-1 md:grid-cols-2 gap-6' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'} p-4 sm:p-6 lg:p-8`}
        >
          {popularPackages.map(pkg => (
            <Link key={pkg.id} to={`/safaris/${pkg.id}`} className="group">
              <div className="bg-white rounded-xl overflow-hidden shadow-soft hover:shadow-large transition-all duration-300 transform hover:-translate-y-1">
                <div
                  className={`relative ${mobile ? 'h-40' : tablet ? 'h-44' : 'h-48'}`}
                >
                  <img
                    src={pkg.image}
                    alt={pkg.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                    <div className="flex items-center gap-2 text-white">
                      <MapPin className={`${mobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                      <span className={`${mobile ? 'text-xs' : 'text-sm'}`}>
                        {pkg.location}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={`${mobile ? 'p-4' : 'p-6'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <Star
                        className={`${mobile ? 'w-3 h-3' : 'w-4 h-4'} text-yellow-400 fill-current`}
                      />
                      <span
                        className={`${mobile ? 'text-xs' : 'text-sm'} font-medium`}
                      >
                        {pkg.rating}
                      </span>
                      <span
                        className={`${mobile ? 'text-xs' : 'text-sm'} text-gray-500`}
                      >
                        ({pkg.reviews})
                      </span>
                    </div>
                    <span
                      className={`${mobile ? 'text-xs' : 'text-sm'} text-gray-500`}
                    >
                      {pkg.duration}
                    </span>
                  </div>
                  <h3
                    className={`${mobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900 group-hover:text-primary transition-colors mb-2`}
                  >
                    {pkg.name}
                  </h3>
                  <p
                    className={`${mobile ? 'text-xs' : 'text-sm'} text-gray-500 mb-3 sm:mb-4 line-clamp-2`}
                  >
                    {pkg.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span
                      className={`${mobile ? 'text-base' : 'text-lg'} font-semibold text-primary`}
                    >
                      {formatCurrency(pkg.price)}
                    </span>
                    <span
                      className={`${mobile ? 'text-xs' : 'text-sm'} text-primary font-medium group-hover:underline`}
                    >
                      View Details
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
